import { z } from 'zod'

/**
 * Summary model for listing plans.
 */
export const PlanSummaryViewModel = z.object({
  id: z.string().describe('Plan ID'),
  title: z.string().describe('Plan title'),
  status: z.enum(['pending', 'in-progress', 'blocked', 'completed']).describe('Overall plan status'),
  tasks: z.object({
    total: z.number().int().min(0).describe('Total tasks count'),
    completed: z.number().int().min(0).describe('Completed tasks count'),
  }).describe('Task completion stats'),
})
export type PlanSummaryViewModel = z.infer<typeof PlanSummaryViewModel>

export const PlanSummaryListViewModel = z.array(PlanSummaryViewModel)
export type PlanSummaryListViewModel = z.infer<typeof PlanSummaryListViewModel>

/**
 * Model for a milestone within a plan.
 */
export const MilestoneViewModel = z.object({
  name: z.string().describe('Milestone name'),
  tasks: z.array(z.string()).describe('Associated task IDs'),
})
export type MilestoneViewModel = z.infer<typeof MilestoneViewModel>

/**
 * Model for a single task item in a plan.
 */
export const PlanTaskItemViewModel = z.object({
  id: z.string().describe('Task ID'),
  text: z.string().describe('Task description'),
  status: z.enum(['pending', 'in-progress', 'blocked', 'done']).default('pending').describe('Task status'),
  dependencies: z.array(z.string()).optional().describe('Dependent task IDs'),
})
export type PlanTaskItemViewModel = z.infer<typeof PlanTaskItemViewModel>

/**
 * Section of tasks in a plan.
 */
export const PlanTaskSectionViewModel = z.object({
  title: z.string().describe('Section title'),
  items: z.array(PlanTaskItemViewModel).describe('List of tasks'),
})
export type PlanTaskSectionViewModel = z.infer<typeof PlanTaskSectionViewModel>

/**
 * Full plan model.
 */
export const PlanViewModel = z.object({
  id: z.string().describe('Plan ID'),
  title: z.string().describe('Plan title'),
  objective: z.string().describe('Plan objective'),
  implementationGoals: z.array(z.string()).describe('List of goals'),
  milestones: z.array(MilestoneViewModel).describe('Plan milestones'),
  taskSections: z.array(PlanTaskSectionViewModel).describe('Task sections'),
})
export type PlanViewModel = z.infer<typeof PlanViewModel>